import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { inject, provideAppInitializer } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { catchError, firstValueFrom, of } from 'rxjs';

import { AppPreset } from './app.preset';
import { routes } from './app.routes';
import { ApiInterceptor } from '@interceptor/api.interceptor';
import { ErrorInterceptor } from '@interceptor/error.interceptor';
import { LoaderInterceptor } from '@interceptor/loader.interceptor';
import { MessageInterceptor } from '@interceptor/message.interceptor';
import { RefreshTokenInterceptor } from '@interceptor/refresh-token.interceptor';
import { AuthService } from '@service/auth.service';
import { GoogleMapsService } from '@service/google-maps.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(async () => {
      const authService = inject(AuthService);

      if (!authService.authToken?.refresh) {
        return;
      }

      try {
        await firstValueFrom(authService.refreshToken(false));
        await firstValueFrom(authService.setCurrentUser());
      } catch (error) {
        console.error('Error during app initialization', error);
      }
    }),
    provideAppInitializer(() => {
      const mapLoaderService = inject(GoogleMapsService);

      return firstValueFrom(
        mapLoaderService.load().pipe(catchError(() => of(undefined))),
      );
    }),
    MessageService,
    ConfirmationService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefreshTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MessageInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(
      withInterceptorsFromDi(),
    ),
    provideRouter(routes),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: AppPreset,
        options: {
          darkModeSelector: '.force-light-mode',
        },
      },
    }),
  ],
};
